import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MagnifyingGlassIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import { pkbApiService } from '../../services/pkbApi';
import { Company, PKBCompanyData } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';

const companySchema = z.object({
    bin: z.string().regex(/^\d{12}$/, 'БИН должен содержать 12 цифр'),
    name: z.string().min(1, 'Название компании обязательно'),
    legalAddress: z.string().min(1, 'Юридический адрес обязателен'),
    actualAddress: z.string().min(1, 'Фактический адрес обязателен'),
    contactPhone: z.string().min(1, 'Контактный телефон обязателен'),
    contactEmail: z.string().email('Неверный формат email'),
    directorName: z.string().min(1, 'ФИО директора обязательно'),
    bank: z.string().min(1, 'Банк обязателен'),
    kbe: z.string().min(1, 'КБе обязательно'),
    bik: z.string().min(1, 'БИК обязательно'),
    accountNumber: z.string().min(1, 'Номер счета обязателен'),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyInfoStepProps {
    company: Company | null;
    onCompanyUpdate: (company: Company) => void;
    isReadOnly?: boolean;
    onValidityChange?: (isValid: boolean) => void;
}

export const CompanyInfoStep: React.FC<CompanyInfoStepProps> = ({
    company,
    onCompanyUpdate,
    isReadOnly = false,
    onValidityChange
}) => {
    const { user, refreshUser } = useAuth();
    const [isSearching, setIsSearching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingCompany, setIsLoadingCompany] = useState(false);
    const [binSearchResult, setBinSearchResult] = useState<PKBCompanyData | null>(null);
    const [binSearchError, setBinSearchError] = useState<string>('');
    const [cachedBinData, setCachedBinData] = useState<{ [key: string]: PKBCompanyData }>({});
    const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        setValue,
        watch,
        reset
    } = useForm<CompanyFormData>({
        resolver: zodResolver(companySchema),
        defaultValues: company || undefined,
        mode: 'onChange'
    });

    const binValue = watch('bin');
    const watchedFields = watch();

    // Проверяем заполненность всех обязательных полей и уведомляем родителя
    useEffect(() => {
        const allFieldsFilled = !!(
            watchedFields.bin && watchedFields.bin.length === 12 &&
            watchedFields.name && watchedFields.name.trim() !== '' &&
            watchedFields.legalAddress && watchedFields.legalAddress.trim() !== '' &&
            watchedFields.actualAddress && watchedFields.actualAddress.trim() !== '' &&
            watchedFields.directorName && watchedFields.directorName.trim() !== '' &&
            watchedFields.bank && watchedFields.bank.trim() !== '' &&
            watchedFields.kbe && watchedFields.kbe.trim() !== '' &&
            watchedFields.bik && watchedFields.bik.trim() !== '' &&
            watchedFields.accountNumber && watchedFields.accountNumber.trim() !== '' &&
            watchedFields.contactPhone && watchedFields.contactPhone.trim() !== '' &&
            watchedFields.contactEmail && watchedFields.contactEmail.trim() !== ''
        );
        
        if (onValidityChange) {
            onValidityChange(allFieldsFilled && isValid);
        }
    }, [watchedFields, isValid, onValidityChange]);

    // Загружаем сохраненные данные компании при монтировании
    useEffect(() => {
        const loadSavedCompanyData = async () => {
            if (user?.companyId && !isLoadingCompany) {
                setIsLoadingCompany(true);
                try {
                    const response = await apiService.getMyCompany();
                    if (response.data.success && response.data.data) {
                        const savedCompany = response.data.data;
                        reset(savedCompany);
                        onCompanyUpdate(savedCompany);
                        console.log('Загружены сохраненные данные компании:', savedCompany);
                    }
                } catch (error) {
                    console.log('Нет сохраненных данных компании или ошибка загрузки:', error);
                } finally {
                    setIsLoadingCompany(false);
                }
            } else if (company && !user?.companyId) {
                reset(company);
            }
        };

        loadSavedCompanyData();
    }, [user?.companyId]); // Только companyId в зависимостях

    // Функция автосохранения
    const autoSaveCompanyData = React.useCallback(async (data: CompanyFormData) => {
        try {
            const response = await apiService.saveCompanyDraft(data);
            if (response.data.success) {
                console.log('Данные автоматически сохранены');
                // Обновляем данные пользователя только если нужно
                if (!user?.companyId) {
                    await refreshUser();
                }
            }
        } catch (error) {
            console.log('Ошибка автосохранения:', error);
        }
    }, [refreshUser, user?.companyId]);

    // Автосохранение при изменении данных
    const watchedData = watch();
    useEffect(() => {
        // Очищаем предыдущий таймер
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        // Проверяем, что форма заполнена хотя бы частично
        if (watchedData.bin && watchedData.bin.length >= 12) {
            const timeout = setTimeout(async () => {
                try {
                    await autoSaveCompanyData(watchedData);
                } catch (error) {
                    console.log('Ошибка автосохранения:', error);
                }
            }, 2000); // Автосохранение через 2 секунды после остановки ввода

            autoSaveTimeoutRef.current = timeout;
        }

        // Cleanup function
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [watchedData, autoSaveCompanyData]);

    // Очистка таймера при размонтировании
    useEffect(() => {
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, []); // Пустой массив зависимостей - выполняется только при размонтировании

    const handleBinSearch = async () => {
        if (!binValue || binValue.length !== 12) {
            toast.error('Введите корректный БИН (12 цифр)');
            return;
        }

        // Проверяем кэш
        if (cachedBinData[binValue]) {
            const cachedResult = cachedBinData[binValue];
            setBinSearchResult(cachedResult);

            // Автозаполнение формы из кэша (без ФИО директора - заполняется вручную)
            setValue('name', cachedResult.name);
            setValue('legalAddress', cachedResult.legalAddress);
            setValue('actualAddress', cachedResult.actualAddress);
            setValue('contactPhone', cachedResult.contactPhone);
            setValue('contactEmail', cachedResult.contactEmail);
            // setValue('directorName', cachedResult.directorName); // Не автозаполняем - пользователь вводит сам
            setValue('bank', cachedResult.bank || '');
            setValue('kbe', cachedResult.kbe || '');
            setValue('bik', cachedResult.bik || '');
            setValue('accountNumber', cachedResult.accountNumber || '');

            toast.success('Данные загружены из кэша (без повторного запроса к ПКБ)');
            return;
        }

        setIsSearching(true);
        setBinSearchResult(null);
        setBinSearchError('');

        try {
            // Сначала проверяем, есть ли компания с таким БИН в базе
            const binCheckResponse = await apiService.checkBin(binValue);

            if (binCheckResponse.data.success) {
                if (binCheckResponse.data.exists) {
                    if (binCheckResponse.data.isOwner) {
                        // Это компания пользователя - загружаем данные
                        const companyData = binCheckResponse.data.companyData;
                        const existingData: PKBCompanyData = {
                            bin: companyData.bin,
                            name: companyData.name || '',
                            legalAddress: companyData.legalAddress || '',
                            actualAddress: companyData.actualAddress || '',
                            contactPhone: companyData.contactPhone || '',
                            contactEmail: companyData.contactEmail || '',
                            directorName: companyData.directorName || '',
                            bank: companyData.bank || '',
                            kbe: companyData.kbe || '',
                            bik: companyData.bik || '',
                            accountNumber: companyData.accountNumber || '',
                            pkbData: {
                                status: { code: '1', ru: 'Зарегистрирован' },
                                founders: { legal: [], individual: [] },
                                activities: [],
                                beneficiaries: []
                            }
                        };

                        setBinSearchResult(existingData);
                        setValue('name', existingData.name);
                        setValue('legalAddress', existingData.legalAddress);
                        setValue('actualAddress', existingData.actualAddress);
                        setValue('contactPhone', existingData.contactPhone);
                        setValue('contactEmail', existingData.contactEmail);
                        // setValue('directorName', existingData.directorName); // Не автозаполняем - пользователь вводит сам
                        setValue('bank', existingData.bank || '');
                        setValue('kbe', existingData.kbe || '');
                        setValue('bik', existingData.bik || '');
                        setValue('accountNumber', existingData.accountNumber || '');

                        toast.success('Загружены данные вашей компании из базы');
                        return;
                    } else {
                        // Компания уже зарегистрирована другим пользователем
                        setBinSearchError('Компания с таким БИН уже зарегистрирована другим пользователем');
                        return;
                    }
                }
            }

            // Всегда делаем запрос к ПКБ API (включая mock режим)
            try {
                const pkbData = await pkbApiService.searchCompanyByBin(binValue);
                const companyData = pkbApiService.transformPKBDataToCompany(pkbData);

                // Сохраняем в кэш
                setCachedBinData(prev => ({
                    ...prev,
                    [binValue]: companyData
                }));

                setBinSearchResult(companyData);

                // Автозаполнение только основных данных компании (без банковских, контактных и ФИО директора)
                setValue('name', companyData.name);
                setValue('legalAddress', companyData.legalAddress);
                setValue('actualAddress', companyData.actualAddress);
                // setValue('directorName', companyData.directorName); // Не автозаполняем - пользователь вводит сам
                // Банковские данные и контакты клиент должен заполнить вручную
                // setValue('contactPhone', companyData.contactPhone);
                // setValue('contactEmail', companyData.contactEmail);
                // setValue('bank', companyData.bank || '');
                // setValue('kbe', companyData.kbe || '');
                // setValue('bik', companyData.bik || '');
                // setValue('accountNumber', companyData.accountNumber || '');

                toast.success('Данные компании загружены из ПКБ. Заполните ФИО директора, банковские реквизиты и контакты вручную.');
                return;

            } catch (pkbError: any) {
                console.error('Ошибка ПКБ API:', pkbError);

                if (pkbError.message.includes('не найдена')) {
                    setBinSearchError('Компания с указанным БИН не найдена в базе ПКБ');
                    return;
                } else if (pkbError.message.includes('авторизации')) {
                    setBinSearchError('Ошибка авторизации в ПКБ');
                    return;
                } else {
                    setBinSearchError(`Ошибка ПКБ: ${pkbError.message}`);
                    return;
                }
            }

        } catch (error: any) {
            console.error('Ошибка поиска по БИН:', error);

            let errorMessage = 'Ошибка при поиске данных компании';

            if (error.message && error.message.includes('HTTP 403')) {
                errorMessage = 'Нет прав доступа. Попробуйте войти в систему заново.';
            } else if (error.message && error.message.includes('HTTP 401')) {
                errorMessage = 'Сессия истекла. Войдите в систему заново.';
            } else if (error.message && error.message.includes('Failed to fetch')) {
                errorMessage = 'Ошибка соединения с сервером. Проверьте интернет-подключение.';
            }

            setBinSearchError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSearching(false);
        }
    };

    const onSubmit = async (data: CompanyFormData) => {
        // Разрешаем обновление контактных данных

        setIsSaving(true);
        try {
            let savedCompany: Company;

            if (company?.id) {
                // Обновляем существующую компанию
                const response = await apiService.updateCompany(company.id, data);
                if (response.data.success) {
                    savedCompany = response.data.data!;
                    toast.success('Информация о компании обновлена');
                } else {
                    throw new Error(response.data.message);
                }
            } else {
                // Сохраняем новую компанию как черновик
                const response = await apiService.saveCompanyDraft(data);
                if (response.data.success) {
                    savedCompany = response.data.data!;
                    toast.success('Информация о компании сохранена');
                    // Обновляем данные пользователя
                    await refreshUser();
                } else {
                    throw new Error(response.data.message);
                }
            }

            onCompanyUpdate(savedCompany);

        } catch (error: any) {
            console.error('Ошибка сохранения компании:', error);

            let message = 'Ошибка сохранения данных компании';

            if (error.response) {
                // Сервер ответил с ошибкой
                console.log('Response status:', error.response.status);
                console.log('Response data:', error.response.data);

                if (error.response.status === 401) {
                    message = 'Сессия истекла. Пожалуйста, войдите в систему заново.';
                    // Очищаем токены и перенаправляем на логин
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('user');
                    setTimeout(() => {
                        window.location.href = '/auth';
                    }, 2000);
                } else if (error.response.status === 403) {
                    message = 'У вас нет прав для выполнения этой операции';
                } else if (error.response.data?.message) {
                    message = error.response.data.message;
                }
            } else if (error.message && error.message.includes('Failed to fetch')) {
                message = 'Ошибка соединения с сервером. Проверьте интернет-подключение.';
            } else if (error.message) {
                message = error.message;
            }

            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* БИН поиск */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-blue-900">Поиск по БИН</h3>
                </div>

                <div className="flex gap-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            БИН компании *
                        </label>
                        <input
                            {...register('bin')}
                            type="text"
                            placeholder="Введите 12-значный БИН"
                            disabled={isReadOnly}
                            className={`input-field ${errors.bin ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                            maxLength={12}
                        />
                        {errors.bin && <p className="form-error">{errors.bin.message}</p>}
                    </div>
                    <div className="flex items-end">
                        <button
                            type="button"
                            onClick={handleBinSearch}
                            disabled={isSearching || !binValue || binValue.length !== 12 || isReadOnly}
                            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                            {isSearching ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Поиск...
                                </>
                            ) : (
                                <>
                                    <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                                    Найти в ПКБ
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {binSearchResult && (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded p-3">
                        <div className="flex items-center">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                            <span className="text-sm font-medium text-green-800">
                                Компания найдена в базе ПКБ
                            </span>
                        </div>
                        <p className="text-sm text-green-700 mt-1">
                            Данные автоматически заполнены. Проверьте и отредактируйте при необходимости.
                        </p>

                        {/* Дополнительная информация из ПКБ */}
                        {binSearchResult.pkbData && (
                            <div className="mt-3 pt-3 border-t border-green-200">
                                <h5 className="text-xs font-medium text-green-800 mb-2">Дополнительная информация из ПКБ:</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-green-700">
                                    <div>
                                        <span className="font-medium">Статус:</span> {binSearchResult.pkbData.status.ru}
                                    </div>
                                    {binSearchResult.pkbData.registrationDate && (
                                        <div>
                                            <span className="font-medium">Дата регистрации:</span> {new Date(binSearchResult.pkbData.registrationDate).toLocaleDateString('ru-RU')}
                                        </div>
                                    )}
                                    {binSearchResult.pkbData.legalForm && (
                                        <div>
                                            <span className="font-medium">ОПФ:</span> {binSearchResult.pkbData.legalForm.ru}
                                        </div>
                                    )}
                                    {binSearchResult.pkbData.entrepreneurshipType && (
                                        <div>
                                            <span className="font-medium">Тип предпринимательства:</span> {binSearchResult.pkbData.entrepreneurshipType.ru}
                                        </div>
                                    )}
                                    {binSearchResult.pkbData.founders.legal.length > 0 && (
                                        <div>
                                            <span className="font-medium">Учредители-юрлица:</span> {binSearchResult.pkbData.founders.legal.length}
                                        </div>
                                    )}
                                    {binSearchResult.pkbData.founders.individual.length > 0 && (
                                        <div>
                                            <span className="font-medium">Учредители-физлица:</span> {binSearchResult.pkbData.founders.individual.length}
                                        </div>
                                    )}
                                    {binSearchResult.pkbData.activities.length > 0 && (
                                        <div className="md:col-span-2">
                                            <span className="font-medium">Виды деятельности:</span> {binSearchResult.pkbData.activities.length}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {binSearchError && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded p-3">
                        <div className="flex items-center">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                            <span className="text-sm font-medium text-red-800">
                                {binSearchError}
                            </span>
                        </div>
                        <p className="text-sm text-red-700 mt-1">
                            Вы можете заполнить данные вручную.
                        </p>
                    </div>
                )}
            </div>

            {/* Форма данных компании */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Название компании *
                        </label>
                        <input
                            {...register('name')}
                            type="text"
                            disabled={true}
                            readOnly={true}
                            className={`input-field bg-gray-100 cursor-not-allowed ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                        {errors.name && <p className="form-error">{errors.name.message}</p>}
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Юридический адрес *
                        </label>
                        <textarea
                            {...register('legalAddress')}
                            rows={3}
                            disabled={true}
                            readOnly={true}
                            className={`input-field bg-gray-100 cursor-not-allowed ${errors.legalAddress ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                        {errors.legalAddress && <p className="form-error">{errors.legalAddress.message}</p>}
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Фактический адрес *
                        </label>
                        <textarea
                            {...register('actualAddress')}
                            rows={3}
                            disabled={true}
                            readOnly={true}
                            className={`input-field bg-gray-100 cursor-not-allowed ${errors.actualAddress ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                        {errors.actualAddress && <p className="form-error">{errors.actualAddress.message}</p>}
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            ФИО директора *
                        </label>
                        <input
                            {...register('directorName')}
                            type="text"
                            placeholder="Введите ФИО директора"
                            disabled={isReadOnly}
                            className={`input-field ${errors.directorName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                        {errors.directorName && <p className="form-error">{errors.directorName.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Банк *
                        </label>
                        <input
                            {...register('bank')}
                            type="text"
                            disabled={false}
                            className={`input-field ${errors.bank ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                            placeholder="Введите банк"
                        />
                        {errors.bank && <p className="form-error">{errors.bank.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            КБе *
                        </label>
                        <input
                            {...register('kbe')}
                            type="text"
                            disabled={false}
                            className={`input-field ${errors.kbe ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                            placeholder="Введите КБе"
                        />
                        {errors.kbe && <p className="form-error">{errors.kbe.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            БИК *
                        </label>
                        <input
                            {...register('bik')}
                            type="text"
                            disabled={false}
                            className={`input-field ${errors.bik ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                            placeholder="Введите БИК"
                        />
                        {errors.bik && <p className="form-error">{errors.bik.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Номер счета *
                        </label>
                        <input
                            {...register('accountNumber')}
                            type="text"
                            disabled={false}
                            className={`input-field ${errors.accountNumber ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                            placeholder="Введите номер счета"
                        />
                        {errors.accountNumber && <p className="form-error">{errors.accountNumber.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Контактный телефон *
                        </label>
                        <input
                            {...register('contactPhone')}
                            type="tel"
                            disabled={false}
                            className={`input-field ${errors.contactPhone ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                        {errors.contactPhone && <p className="form-error">{errors.contactPhone.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email компании *
                        </label>
                        <input
                            {...register('contactEmail')}
                            type="email"
                            disabled={false}
                            className={`input-field ${errors.contactEmail ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        />
                        {errors.contactEmail && <p className="form-error">{errors.contactEmail.message}</p>}
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={isSaving}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? 'Сохранение...' : 'Обновить контактные данные'}
                    </button>
                </div>
            </form>

            {isReadOnly && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4">
                    <p className="text-sm text-gray-600 text-center">
                        Информация о компании (только для просмотра)
                    </p>
                </div>
            )}
        </div>
    );
};
